import { useAuditMatchPlaybackControl } from '@app/modules/audit/modules/station-audit/hooks/useAuditMatchPlaybackControl';
import { AuditMatch } from '@app/modules/audit/modules/station-audit/types';
import PauseIcon from '@mui/icons-material/Pause';
import PlayIcon from '@mui/icons-material/PlayArrow';
import { IconButton, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useAuditMarkerCapture } from './useAuditMarkerCapture';

const playMessage = defineMessage({
  id: 'common.play',
  defaultMessage: 'Reproducir',
});

const pauseMessage = defineMessage({
  id: 'common.pause',
  defaultMessage: 'Pausar',
});

type AuditMatchPlayPauseButtonProps = {
  match: AuditMatch;
};

/**
 * Controls audit-match playback without closing the popup, allowing pause from the same action.
 */
export function AuditMatchPlayPauseButton({ match }: AuditMatchPlayPauseButtonProps) {
  const { $t } = useIntl();
  const { showPause, handleClick } = useAuditMatchPlaybackControl(match);
  const capture = useAuditMarkerCapture();
  const tooltip = showPause ? $t(pauseMessage) : $t(playMessage);

  const onClick = useCallback(() => {
    capture({
      action: showPause ? 'pause' : 'play',
      kind: 'spot',
      // Spot list schema has no id; customId + datetime uniquely identify the match.
      marker_id: match.audio?.customId
        ? `${match.audio.customId}:${match.datetime}`
        : match.datetime,
    });
    handleClick();
  }, [capture, showPause, match.audio?.customId, match.datetime, handleClick]);

  return (
    <Tooltip title={tooltip} placement="top">
      <IconButton size="small" color="secondary" onClick={onClick}>
        {showPause ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}
